import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Loader2, FileText } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { STEW_TOOL_REGISTRY, type StewToolKey } from "@/lib/stewToolRegistry";
import { docHref } from "@/lib/sopLinks";
import { stripDocIdPrefix } from "@/lib/docTitle";
import { stripStartHerePrefix } from "@/lib/docTypeLabel";

/**
 * Machinery: type + key. Display: label (and, for docs, the live title).
 * A doc action never renders its doc_id to a member.
 */
export type RelatedAction = {
  type: "workflow" | "sample" | "doc";
  key: string;
  label?: string;
};

export function parseRelatedActions(value: unknown): RelatedAction[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (a: any) =>
      a &&
      typeof a === "object" &&
      (a.type === "workflow" || a.type === "sample" || a.type === "doc") &&
      typeof a.key === "string" &&
      a.key.length > 0,
  ) as RelatedAction[];
}

type Resolved = {
  action: RelatedAction;
  label: string;
  icon: typeof FileText;
};

export function RelatedActions({ actions }: { actions: RelatedAction[] }) {
  const navigate = useNavigate();
  const [resolved, setResolved] = useState<Resolved[]>([]);
  const [docPaths, setDocPaths] = useState<Record<string, string>>({});
  const [samplePaths, setSamplePaths] = useState<Record<string, string>>({});
  const [busyKey, setBusyKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const docKeys = actions.filter((a) => a.type === "doc").map((a) => a.key);
      const sampleKeys = actions.filter((a) => a.type === "sample").map((a) => a.key);

      const docMap: Record<string, { title: string; href: string }> = {};
      if (docKeys.length) {
        const { data } = await supabase
          .from("sop_documents" as any)
          .select("doc_id,title,slug,collection,status")
          .in("doc_id", docKeys)
          .eq("status", "published");
        for (const r of (data as any[]) || []) {
          docMap[r.doc_id] = {
            title: stripStartHerePrefix(stripDocIdPrefix(r.title)),
            href: docHref({ doc_id: r.doc_id, collection: r.collection, slug: r.slug }),
          };
        }
      }

      const sampleMap: Record<string, { title: string; path: string }> = {};
      if (sampleKeys.length) {
        const { data } = await supabase
          .from("sample_documents" as any)
          .select("id,title,storage_path")
          .in("id", sampleKeys);
        for (const r of (data as any[]) || []) {
          sampleMap[r.id] = { title: r.title, path: r.storage_path };
        }
      }

      const out: Resolved[] = [];
      const hrefs: Record<string, string> = {};
      const spaths: Record<string, string> = {};
      for (const a of actions) {
        if (a.type === "workflow") {
          const tool = STEW_TOOL_REGISTRY[a.key as StewToolKey];
          if (!tool) continue;
          out.push({ action: a, label: a.label || tool.label, icon: tool.icon });
        } else if (a.type === "doc") {
          const d = docMap[a.key];
          if (!d) continue;
          hrefs[a.key] = d.href;
          out.push({ action: a, label: d.title || a.label || "Open document", icon: FileText });
        } else {
          const s = sampleMap[a.key];
          if (!s) continue;
          spaths[a.key] = s.path;
          out.push({ action: a, label: a.label || s.title, icon: FileText });
        }
      }
      if (cancelled) return;
      setDocPaths(hrefs);
      setSamplePaths(spaths);
      setResolved(out);
    })();
    return () => {
      cancelled = true;
    };
  }, [JSON.stringify(actions)]);

  async function run(r: Resolved) {
    const { action } = r;
    if (action.type === "workflow") {
      const tool = STEW_TOOL_REGISTRY[action.key as StewToolKey];
      if (tool) navigate(tool.route);
      return;
    }
    if (action.type === "doc") {
      const href = docPaths[action.key];
      if (href) navigate(href);
      return;
    }
    const path = samplePaths[action.key];
    if (!path) return;
    setBusyKey(action.key);
    try {
      const { data, error } = await supabase.storage
        .from("sample-documents")
        .createSignedUrl(path, 300);
      if (error || !data?.signedUrl) throw error;
      window.open(data.signedUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Sample open failed:", err);
    } finally {
      setBusyKey(null);
    }
  }

  if (resolved.length === 0) return null;

  return (
    <div className="mt-8">
      <p className="text-sm font-medium mb-3">Next steps</p>
      <div className="flex flex-col sm:flex-row flex-wrap gap-2">
        {resolved.map((r) => {
          const Icon = r.icon;
          const busy = busyKey === r.action.key;
          return (
            <button
              key={`${r.action.type}:${r.action.key}`}
              type="button"
              onClick={() => run(r)}
              disabled={busy}
              className="inline-flex min-h-[44px] w-full sm:w-auto items-center justify-between gap-2 rounded-full border border-border/60 bg-background hover:bg-muted transition-colors px-4 py-2 text-sm font-medium text-foreground shadow-sm disabled:opacity-60"
            >
              <span className="inline-flex items-center gap-2 min-w-0">
                {busy ? (
                  <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" />
                ) : (
                  <Icon className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                )}
                <span className="truncate">{r.label}</span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            </button>
          );
        })}
      </div>
    </div>
  );
}
